import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FornitoriAdmin } from 'src/app/models/supplier-portal/supplier/fornitori-admin.models';
import { FornitoriAdminMainService } from 'src/app/services/fornitori-admin-main.service';
import { TraceSearchStateService } from 'src/app/services/trace-search-state.service';

@Component({
  selector: 'app-tracciabilita-fornitori',
  standalone: false,
  templateUrl: './tracciabilita-fornitori.component.html',
  styleUrls: ['./tracciabilita-fornitori.component.scss'],
})


export class TracciabilitaFornitoriComponent implements OnInit {

  public results: FornitoriAdmin.TraceSearchResult[] = [];
  public loading = false;
  public searched = false;
  public searchError = false;
  public filterModalOpen = false;

  public searchTerm = '';
  public startDate: Date | null = null;
  public endDate: Date | null = null;

  /**
   * Mapping `codice_fornitore` → ragione sociale fornitore.
   *
   * Popolato una volta al mount da `getSupplierAccounts()` (stessa
   * fonte usata dalla pagina Account Fornitori). Serve come fallback
   * per il nome del fornitore quando `getTraceSuppliers` non ce lo
   * restituisce (o quando il payload ha solo il codice).
   */
  private supplierNameMap = new Map<string, string>();

  /**
   * Mapping `product_code` → fornitore associato a quell'articolo.
   *
   * v2.14.4 (PM request 2026-07-14): il payload di
   * `search_trace_products` NON contiene informazioni sul fornitore
   * (nessun `codice_fornitore` né `fornitore_id`). Per popolare le
   * colonne "Numero fornitore" e "Nome fornitore" senza aspettare
   * un allargamento del BE, per ogni articolo unico nei risultati
   * chiamiamo `getTraceSuppliers(product_code)` e prendiamo il
   * primo fornitore restituito.
   *
   * ⚠️ TODO tech debt / ticket BE aperto: se un articolo è fornito
   * da PIÙ fornitori diversi, questo workaround mostra sempre il
   * primo di quella lista (che è deterministic BE-side ma NON
   * necessariamente è il fornitore dell'ordine specifico visualizzato
   * in quella riga). Fix definitivo: chiedere a Robert di aggiungere
   * `codice_fornitore` (o `fornitore_id`) al payload di
   * `search_trace_products` — allora questo mapping diventa
   * `r.codice_fornitore` diretto e le chiamate extra `getTraceSuppliers`
   * si eliminano.
   */
  private productSupplierMap = new Map<string, { code: string; name: string }>();

  constructor(
    private readonly fornitoriAdminServ: FornitoriAdminMainService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly traceState: TraceSearchStateService
  ) { }

  ngOnInit(): void {
    // Initialization logic if needed

    const state = this.traceState.getState();

    this.searchTerm = state.searchTerm;
    this.startDate = state.startDate;
    this.endDate = state.endDate;
    this.results = [...state.results];
    this.searched = state.searched;
    this.searchError = state.searchError;

    this.fornitoriAdminServ.getTraceProducts().subscribe({
      next: (data) => {
        console.log('Trace products:', data);
      },
      error: (err) => {
        console.error('Error fetching trace products:', err);
      },
    });

    this.loadSuppliersMap();

    // v2.14.4: se rientriamo con risultati persistiti dallo state
    // service (es. tornando indietro da dettaglio tracking), popola
    // subito la productSupplierMap così le colonne non lampeggiano
    // "—" al mount.
    if (this.results.length > 0) {
      this.loadSuppliersForProducts(this.results);
    }
  }

  /**
   * Carica l'anagrafica fornitori una volta e costruisce la Map
   * `codice_fornitore` → `fornitore`. In caso di errore silenzia
   * (le colonne Nome fornitore mostreranno `—` come fallback).
   *
   * Usiamo `getSupplierAccounts()` — stesso metodo che alimenta la
   * pagina Account Fornitori — che internamente chiama
   * `getSuppliersList()` senza params e mappa `response.items`.
   * Questo evita il pattern GET-con-body-non-vuoto che HttpClient
   * non emette in rete (invisibile in DevTools Network).
   */
  private loadSuppliersMap(): void {
    this.fornitoriAdminServ
      .getSupplierAccounts()
      .subscribe({
        next: (items) => {
          this.supplierNameMap.clear();
          for (const item of items ?? []) {
            if (item.codice_fornitore) {
              this.supplierNameMap.set(item.codice_fornitore, item.fornitore ?? '');
            }
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.warn('[Tracciabilità] load suppliers map failed:', err);
        },
      });
  }

  /**
   * Popola `productSupplierMap` per gli articoli unici nei risultati.
   *
   * Per ogni articolo unico, chiama `getTraceSuppliers(product_code)`
   * in parallelo (forkJoin) e memorizza il primo fornitore restituito.
   * Se un articolo era già nella map (da una search precedente) lo
   * riusiamo — evitiamo chiamate ridondanti.
   *
   * L'errore su un singolo articolo non blocca gli altri: fallback
   * a `of([])` così quell'articolo resta con celle "—".
   */
  private loadSuppliersForProducts(products: FornitoriAdmin.TraceSearchResult[]): void {
    const uniqueCodes = Array.from(new Set(
      products.map((p) => p.product_code).filter(Boolean),
    ));
    const missing = uniqueCodes.filter((code) => !this.productSupplierMap.has(code));
    if (missing.length === 0) {
      this.cdr.detectChanges();
      return;
    }

    const lookups = missing.map((code) =>
      this.fornitoriAdminServ.getTraceSuppliers(code).pipe(
        catchError(() => of([] as FornitoriAdmin.TraceSupplier[])),
      ),
    );

    forkJoin(lookups).subscribe({
      next: (results) => {
        results.forEach((suppliers, i) => {
          const code = missing[i];
          const first = suppliers?.[0];
          if (first?.codice_fornitore) {
            // Fallback nome: prima da getTraceSuppliers, poi dalla
            // lista fornitori (che ha il payload completo), poi '—'.
            const name = first.fornitore
              || this.supplierNameMap.get(first.codice_fornitore)
              || '';
            this.productSupplierMap.set(code, {
              code: first.codice_fornitore,
              name,
            });
          }
        });
        this.cdr.detectChanges();
      },
    });
  }

  /** Restituisce il codice fornitore per il product_code della riga. */
  supplierCode(productCode: string): string {
    return this.productSupplierMap.get(productCode)?.code || '—';
  }

  /** Restituisce la ragione sociale del fornitore per il product_code. */
  supplierName(productCode: string): string {
    return this.productSupplierMap.get(productCode)?.name || '—';
  }

  search(): void {
    if (!this.searchTerm && !this.startDate) return;

    const input = new FornitoriAdmin.TraceSearchInput();

    if (this.searchTerm) {
      if (this.searchTerm.includes('$')) {
        input.art_qr = this.searchTerm;
      } 
      else {
        input.art_code = this.searchTerm;
      }
    }

    if (this.startDate) input.date_from = this.formatDate(this.startDate);
    if (this.endDate) input.date_to = this.formatDate(this.endDate);

    this.loading = true;
    this.searched = true;
    this.searchError = false;
    this.fornitoriAdminServ.searchTraceProducts(input).subscribe({
      next: (data) => {
        this.results = data;
        this.loading = false;

        // v2.14.4: lookup fornitore per articolo unico (workaround
        // finché `search_trace_products` non espone codice_fornitore
        // direttamente — cfr. doc su `productSupplierMap`).
        this.loadSuppliersForProducts(data);

        this.traceState.setState({
          searchTerm: this.searchTerm,
          startDate: this.startDate,
          endDate: this.endDate,
          results: [...this.results],
          searched: this.searched,
          searchError: this.searchError,
        });

        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.searchError = true;
        this.cdr.detectChanges();
      },
    });
  }

  onOpenDetail(item: FornitoriAdmin.TraceSearchResult): void {
    this.traceState.setState({
      searchTerm: this.searchTerm,
      startDate: this.startDate,
      endDate: this.endDate,
      results: [...this.results],
      searched: this.searched,
      searchError: this.searchError,
    });

    this.filterModalOpen = false;
    this.router.navigate(
      ['/fornitori-tracking', encodeURIComponent(item.product_qr)],
      { queryParams: { order: item.order_number } },
    );
  }

  retrySearch(): void {
    this.searchError = false;
    this.search();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.startDate = null;
    this.endDate = null;
    this.results = [];
    this.searched = false;
    this.searchError = false;
    this.filterModalOpen = false;
    this.traceState.clear();
  }

  formatLot(year: number, week: number): string {
    if (!year && !week) return '-';
    return `${week}/${String(year).padStart(2, '0')}`;
  }

  formatDate(date: Date): string {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  // ── Date binding helpers ────────────────────────────────────────
  // Native `<input type="date">` exchanges values as 'yyyy-mm-dd'
  // strings; `search()` works on real `Date` objects. These bridge
  // the two (replaces the legacy `<kendo-datepicker>` binding).
  get startDateStr(): string { return this.toInputDate(this.startDate); }
  set startDateStr(value: string) { this.startDate = this.fromInputDate(value); }
  get endDateStr(): string { return this.toInputDate(this.endDate); }
  set endDateStr(value: string) { this.endDate = this.fromInputDate(value); }

  /** True when at least one filter narrows the search. */
  get filtersActive(): boolean {
    return !!this.searchTerm.trim() || !!this.startDate || !!this.endDate;
  }

  private toInputDate(d: Date | null): string {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  private fromInputDate(value: string): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  
}
